import { getFragmentContent } from "../../Pages/Fragments/fragmentsClient";

/**
 * Block-fragment preview overlay. The home dashboard widgets : the MarketsBoard cards (cours de change /
 * inflation / taux d'intérêt), the FxRatesExplorer converter, the InformationHub and the governors roster : * are block-fragments: at deploy time their page slot is reduced to `{ fragmentKey }` and the real data
 * lives in one CDN file `fragments/{lang}/{key}.json` (see ContentService.ConvertBlockFragmentRefs /
 * BccBlockFragments). The public site fills the widget from that file at request time
 * (resolveDynamicComponents).
 *
 * For the admin page-editor preview to MATCH what will deploy : instead of re-projecting one widget
 * (inflation) client-side from the DB while the others render blank : we fetch those SAME baked fragment
 * files and overlay them onto the previewed components here. So every computed widget shows its live
 * fragment data, uniformly, and updates whenever the fragment re-bakes (a data change).
 */

/** Stable block-fragment keys : mirror BccBlockFragmentKeys (C#). */
export const BLOCK_FRAGMENT_KEYS = {
	marketsExchange: "markets-exchange",
	marketsInflation: "markets-inflation",
	marketsInterest: "markets-interest",
	keyIndicatorsBar: "key-indicators-bar",
	informationHub: "information-hub",
	fxRatesExplorer: "fx-rates-explorer",
	governors: "governors"
} as const;

export const ALL_BLOCK_FRAGMENT_KEYS: ReadonlyArray<string> = Object.values(BLOCK_FRAGMENT_KEYS);

export type BlockFragmentMap = Record<string, Record<string, unknown>>;

/** MarketsBoard sub-block field → fragment key (the deploy-time slot mapping). */
const MARKETS_SUBBLOCK_KEYS: Record<string, string> = {
	exchange: BLOCK_FRAGMENT_KEYS.marketsExchange,
	inflation: BLOCK_FRAGMENT_KEYS.marketsInflation,
	interest: BLOCK_FRAGMENT_KEYS.marketsInterest
};

/** Standalone component type → fragment key (the component's whole content is the fragment). */
const COMPONENT_KEYS: Record<string, string> = {
	FxRatesExplorer: BLOCK_FRAGMENT_KEYS.fxRatesExplorer,
	InformationHub: BLOCK_FRAGMENT_KEYS.informationHub,
	GovernorList: BLOCK_FRAGMENT_KEYS.governors
};

/**
 * Fetch every block-fragment's baked content for an environment, in parallel. Fragments that are not
 * baked (or fail to load) are simply absent from the map : the overlay then leaves that slot untouched,
 * so the SDK renderer shows its "déployé" placeholder exactly as the live site would on a miss.
 */
export async function loadBlockFragments(
	environment: "preview" | "production",
	language = "fr"
): Promise<BlockFragmentMap> {
	const entries = await Promise.all(
		ALL_BLOCK_FRAGMENT_KEYS.map(async (key) => [key, await getFragmentContent(key, environment, language)] as const)
	);
	const map: BlockFragmentMap = {};
	for (const [key, value] of entries) {
		if (value && typeof value === "object") map[key] = value as Record<string, unknown>;
	}
	return map;
}

/**
 * Overlay the baked block-fragments onto one previewed component. Returns a component whose computed
 * widgets carry the SAME data the live site renders; a component with no block-fragment slot (or whose
 * fragment isn't baked) is returned unchanged.
 */
export function overlayBlockFragments<T extends { componentType?: string; content?: unknown }>(
	component: T,
	fragments: BlockFragmentMap
): T {
	const type = component.componentType;
	if (!type) return component;

	if (type === "MarketsBoard") {
		const content = { ...((component.content as Record<string, unknown>) ?? {}) };
		let changed = false;
		for (const [field, key] of Object.entries(MARKETS_SUBBLOCK_KEYS)) {
			if (content[field] != null && fragments[key]) {
				content[field] = fragments[key];
				changed = true;
			}
		}
		return changed ? { ...component, content } : component;
	}

	const componentKey = COMPONENT_KEYS[type];
	if (componentKey && fragments[componentKey]) {
		return { ...component, content: fragments[componentKey] };
	}
	return component;
}

/*
 * ---- Card-array fragments (latest-articles, latest-{type}) --------------------------------
 * A second fragment family: at deploy time NewsSection / LatestArticleGrid slots are reduced to
 * `{ fragmentKey, maxItems }` and the fragment file holds an ARRAY of cards
 * (`[{ contentKey, data: { title, description, imageSrc, href, … } }]`). The public site fills
 * those in resolveDynamicComponents (BCC.PUBLIC.Nextjs); the admin preview historically only
 * handled the block-OBJECT family above, so these components rendered blank. The functions
 * below port the public mapping so the preview matches deployment for cards too.
 */

export type CardFragmentMap = Record<string, Array<Record<string, unknown>>>;

/** Card fragment keys referenced by the page: every `content.fragmentKey` that is NOT a block-object key. */
export function collectCardFragmentKeys(
	components: ReadonlyArray<{ content?: unknown }>
): string[] {
	const keys = new Set<string>();
	for (const component of components ?? []) {
		const content = component?.content as Record<string, unknown> | undefined;
		const key = content?.fragmentKey;
		if (typeof key === "string" && key && !ALL_BLOCK_FRAGMENT_KEYS.includes(key)) {
			keys.add(key);
		}
	}
	return Array.from(keys);
}

/** Fetch the referenced card fragments in parallel; non-array (or missing) contents are skipped. */
export async function loadCardFragments(
	keys: ReadonlyArray<string>,
	environment: "preview" | "production",
	language = "fr"
): Promise<CardFragmentMap> {
	const entries = await Promise.all(
		keys.map(async (key) => [key, await getFragmentContent(key, environment, language)] as const)
	);
	const map: CardFragmentMap = {};
	for (const [key, value] of entries) {
		if (Array.isArray(value)) map[key] = value as Array<Record<string, unknown>>;
	}
	return map;
}

/** Same key bridge as the public `toNewsItem`: collection cards store description/imageSrc, the news renderers read excerpt/image. */
const toNewsItem = (item: Record<string, unknown>): Record<string, unknown> => ({
	...item,
	excerpt: item.excerpt ?? item.description ?? item.summary ?? "",
	image: item.image ?? item.imageSrc
});

/** Governor strips render 4-up : mirror of the public GOVERNOR_HISTORIQUE_FRAGMENT_KEY rule. */
const GOVERNOR_FRAGMENT_KEY = "latest-gouverneur";

/**
 * Fill one previewed component from its card-array fragment: NewsSection/HomeNews get the
 * featured (first card) + items (rest) split; every other consumer gets a plain `items` fill : * the exact shaping resolveDynamicComponents applies on the live site. A component without a
 * `fragmentKey`, or whose fragment isn't loaded, passes through unchanged (renders its
 * placeholder, as on a live miss). No self-exclusion here: the editor previews the page itself.
 */
export function overlayCardFragments<T extends { componentType?: string; content?: unknown }>(
	component: T,
	cards: CardFragmentMap
): T {
	const content = component?.content as Record<string, unknown> | undefined;
	const key = content?.fragmentKey;
	if (typeof key !== "string" || !cards[key]) return component;

	let items = cards[key]
		.filter((entry) => entry && typeof entry === "object" && "data" in entry)
		.map((entry) => {
			const data = ((entry as { data?: Record<string, unknown> }).data ?? {}) as Record<string, unknown>;
			return { ...data, id: data.id ?? (entry as { contentKey?: unknown }).contentKey };
		});

	let cap = Number(content?.maxItems ?? content?.limit ?? content?.count ?? 0) || 0;
	if (key === GOVERNOR_FRAGMENT_KEY) cap = Math.max(cap, 4);
	if (cap > 0) items = items.slice(0, cap);

	const next: Record<string, unknown> = { ...content };
	if (component.componentType === "NewsSection" || component.componentType === "HomeNews") {
		if (items.length > 0) {
			next.featured = toNewsItem(items[0]);
			next.items = items.slice(1).map(toNewsItem);
		} else {
			next.items = [];
		}
	} else {
		next.items = items;
	}
	return { ...component, content: next };
}
